import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  isIndiaCountry,
  isValidIndianPincode,
  fetchIndiaPincodeDetails,
  fetchIndiaServiceability,
  normalizePincode
} from '../utils/indiaPincode';

const Addresses = () => {
  const navigate = useNavigate();
  const { getToken, logout } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || '';
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    type: 'shipping',
    first_name: '',
    last_name: '',
    company: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    district: '',
    zip_code: '',
    country: 'India',
    phone: '',
    email: '',
    is_default: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [manualLocationEdit, setManualLocationEdit] = useState(false);
  const [pincodeLookup, setPincodeLookup] = useState({ loading: false, error: '', data: null });
  const [serviceability, setServiceability] = useState({ loading: false, message: '', configured: false, serviceable: true });

  const fetchAddresses = useCallback(async () => {
    try {
      const token = getToken(false); // E-commerce token
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/api/addresses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        logout(false);
        navigate('/login');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setAddresses(data.data);
      } else if (data.message) {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getToken, logout, navigate]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      type: 'shipping',
      first_name: '',
      last_name: '',
      company: '',
      street: '',
      landmark: '',
      city: '',
      state: '',
      district: '',
      zip_code: '',
      country: 'India',
      phone: '',
      email: '',
      is_default: true
    });
    setEditingId(null);
    setShowForm(false);
    setError('');
    setManualLocationEdit(false);
    setPincodeLookup({ loading: false, error: '', data: null });
    setServiceability({ loading: false, message: '', configured: false, serviceable: true });
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (address) => {
    setFormData({
      type: address.type || 'shipping',
      first_name: address.first_name || '',
      last_name: address.last_name || '',
      company: address.company || '',
      street: address.street || '',
      landmark: address.landmark || '',
      city: address.city || '',
      state: address.state || '',
      district: address.district || '',
      zip_code: address.zip_code || '',
      country: address.country || 'India',
      phone: address.phone || '',
      email: address.email || '',
      is_default: address.is_default || false
    });
    setEditingId(address.id);
    setShowForm(true);
    setError('');
    setManualLocationEdit(false);
    setPincodeLookup({ loading: false, error: '', data: null });
    setServiceability({ loading: false, message: '', configured: false, serviceable: true });
  };

  // India pincode lookup + autofill
  useEffect(() => {
    const apiBase = API_BASE;
    const isIndia = isIndiaCountry(formData.country);
    const pincode = normalizePincode(formData.zip_code);

    if (!isIndia) {
      setPincodeLookup({ loading: false, error: '', data: null });
      setServiceability({ loading: false, message: '', configured: false, serviceable: true });
      return;
    }

    if (!pincode) {
      setPincodeLookup({ loading: false, error: '', data: null });
      setServiceability({ loading: false, message: '', configured: false, serviceable: true });
      return;
    }

    if (!isValidIndianPincode(pincode)) {
      setPincodeLookup({ loading: false, error: 'Enter a valid 6-digit pincode', data: null });
      setServiceability({ loading: false, message: '', configured: false, serviceable: true });
      setManualLocationEdit(true);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setPincodeLookup(prev => ({ ...prev, loading: true, error: '' }));
        const data = await fetchIndiaPincodeDetails({ apiBase, pincode });
        if (cancelled) return;
        setPincodeLookup({ loading: false, error: '', data });

        if (!manualLocationEdit) {
          const firstOffice = data?.postOffices?.[0];
          setFormData(prev => ({
            ...prev,
            state: data.state || prev.state,
            district: data.district || prev.district,
            city: prev.city || firstOffice?.name || ''
          }));
        }

        setServiceability(prev => ({ ...prev, loading: true, message: '' }));
        const svc = await fetchIndiaServiceability({ apiBase, pincode });
        if (cancelled) return;
        setServiceability({
          loading: false,
          configured: !!svc?.configured,
          serviceable: svc?.serviceable !== false,
          message: svc?.configured && svc?.serviceable === false
            ? 'This pincode may be out of delivery range.'
            : ''
        });
      } catch (e) {
        if (cancelled) return;
        setPincodeLookup({ loading: false, error: e.message || 'Failed to lookup pincode', data: null });
        setServiceability({ loading: false, message: '', configured: false, serviceable: true });
        setManualLocationEdit(true);
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [API_BASE, formData.country, formData.zip_code, manualLocationEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = getToken(false); // E-commerce token
      if (!token) {
        navigate('/login');
        return;
      }
      const url = editingId 
        ? `${API_BASE}/api/addresses/${editingId}`
        : `${API_BASE}/api/addresses`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        logout(false);
        navigate('/login');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(editingId ? 'Address updated successfully' : 'Address added successfully');
        resetForm();
        fetchAddresses();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save address');
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const token = getToken(false); // E-commerce token
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await fetch(`${API_BASE}/api/addresses/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        logout(false);
        navigate('/login');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSuccess('Address deleted successfully');
        fetchAddresses();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to delete address');
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      setError('Failed to delete address');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const token = getToken(false); // E-commerce token
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/addresses/${id}/default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        logout(false);
        navigate('/login');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSuccess('Default address updated');
        fetchAddresses();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error setting default:', error);
      setError('Failed to set default address');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading addresses...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Addresses</h1>
          <p className="mt-2 text-gray-600">Manage your shipping and billing addresses</p>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            New addresses are saved as your default delivery address.
          </div>
          <button
            onClick={handleAddNew}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            + Add Another Address
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Add New Button */}
        {!showForm && (
          <div className="mb-6">
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
            >
              + Add New Address
            </button>
          </div>
        )}

        {/* Address Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingId ? 'Edit Address' : 'Add New Address'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="shipping">Shipping</option>
                    <option value="billing">Billing</option>
                  </select>
                </div>

                {editingId && (
                  <div className="flex items-center">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={formData.is_default}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Set as default address</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company (Optional)
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address *
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Landmark (Optional)
                </label>
                <input
                  type="text"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Near..."
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isIndiaCountry(formData.country) && pincodeLookup.loading && (
                    <p className="text-xs text-gray-500 mt-1">Looking up pincode…</p>
                  )}
                  {isIndiaCountry(formData.country) && pincodeLookup.error && (
                    <p className="text-xs text-red-600 mt-1">{pincodeLookup.error}</p>
                  )}
                  {isIndiaCountry(formData.country) && serviceability.message && (
                    <p className="text-xs text-amber-700 mt-1">{serviceability.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isIndiaCountry(formData.country) ? 'Locality / Area *' : 'City *'}
                  </label>
                  {isIndiaCountry(formData.country) && pincodeLookup.data?.postOffices?.length ? (
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Locality</option>
                      {pincodeLookup.data.postOffices.map((po) => (
                        <option key={`${po.name}-${po.block}`} value={po.name}>
                          {po.name}{po.block ? `, ${po.block}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    disabled={isIndiaCountry(formData.country) && !manualLocationEdit}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  />
                </div>
              </div>

              {isIndiaCountry(formData.country) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      District
                    </label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      disabled={!manualLocationEdit}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={manualLocationEdit}
                        onChange={(e) => setManualLocationEdit(e.target.checked)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Edit state/district manually</span>
                    </label>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country *
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="India">India</option>
                  <option value="United Arab Emirates">United Arab Emirates (UAE)</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
                >
                  {editingId ? 'Update Address' : 'Save Address'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Address List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {addresses.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-600">No addresses saved yet</p>
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 text-blue-600 hover:text-blue-800"
              >
                Add your first address
              </button>
            </div>
          ) : (
            addresses.map((address) => (
              <div
                key={address.id}
                className={`bg-white rounded-lg shadow-md p-6 relative ${
                  address.is_default ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {address.is_default && (
                  <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Default
                  </span>
                )}

                <div className="mb-4">
                  <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                    {address.type === 'shipping' ? 'Shipping' : 'Billing'}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-gray-900">
                    {address.first_name} {address.last_name}
                  </p>
                  {address.company && (
                    <p className="text-gray-600">{address.company}</p>
                  )}
                  <p className="text-gray-600">{address.street}</p>
                  <p className="text-gray-600">
                    {address.city}, {address.state} {address.zip_code}
                  </p>
                  <p className="text-gray-600">{address.country}</p>
                  {address.phone && (
                    <p className="text-gray-600">Phone: {address.phone}</p>
                  )}
                  {address.email && (
                    <p className="text-gray-600">Email: {address.email}</p>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                  <button
                    onClick={() => handleEdit(address)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Edit
                  </button>
                  {!address.is_default && (
                    <>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => handleSetDefault(address.id)}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Set Default
                      </button>
                    </>
                  )}
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => handleDelete(address.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Addresses;
